import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../services/api";
import { CheckCircle2, Inbox, PlusCircle, Activity, Loader2, Headset } from "lucide-react";
import { parseApiDateTime } from "../utils/datetime";

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">{label}</p>
      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${tone}`}>
        <Icon size={18} />
      </span>
    </div>
    <p className="mt-2 text-3xl font-bold text-foreground">{value}</p>
  </div>
);

function fmt(iso) {
  const d = parseApiDateTime(iso);
  if (!d) return "";
  return d.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const MinhaAtividade = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/me/activity")
      .then((r) => {
        if (alive) setData(r.data || {}); // evita crash de render se vier vazio
      })
      .catch((e) => {
        if (alive) setError(e.response?.data?.detail || "Erro ao carregar sua atividade.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Layout>
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="animate-spin" size={18} /> Carregando…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <StatCard icon={Headset} label="Atendidos (desde o cadastro)" value={data.attended_total} tone="bg-indigo-100 text-indigo-600" />
            <StatCard icon={CheckCircle2} label="Encerrados este mês" value={data.closed_this_month} tone="bg-green-100 text-green-600" />
            <StatCard icon={Inbox} label="Atribuídos a mim (abertos)" value={data.assigned_open} tone="bg-blue-100 text-blue-600" />
            <StatCard icon={PlusCircle} label="Abertos por mim" value={data.created_count} tone="bg-sky-100 text-sky-600" />
            <StatCard icon={Activity} label="Ações no sistema" value={data.actions_total} tone="bg-amber-100 text-amber-600" />
          </div>

          <div className="rounded-xl border border-border bg-card shadow-sm">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="text-lg font-bold text-foreground">Suas últimas ações</h2>
            </div>
            {(!data.recent || data.recent.length === 0) ? (
              <p className="px-6 py-8 text-sm text-muted-foreground text-center">
                Nenhuma ação registrada ainda.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {data.recent.map((a, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => navigate(`/tickets/${a.ticket_id}`)}
                      className="w-full text-left px-6 py-3 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {a.description || "Ação"}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            #{a.ticket_id} · {a.ticket_title}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{fmt(a.at)}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default MinhaAtividade;
